/* eslint-disable react/destructuring-assignment */
import { useSelector } from 'react-redux';
import { Carousel, Row, Col, Affix } from 'antd';
import { useState, useEffect } from 'react';

import { partnersandAds, santamAds, avivaAds, axaAds, usAds } from './assets';
import theme, { THEMES } from '../../services/theme';
import useWindowResize from '../hooks/useWindowResize';
import useWindowScroll from '../hooks/useWindowScroll';
import './ads.less';

const BANNERS_API_URL =
  'https://lmicdn.blob.core.windows.net/common/banners/side_ads.json';

// Custom hook to fetch banner data
const useBannersData = () => {
  const [bannersData, setBannersData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(BANNERS_API_URL);

        const data = await response.json();
        const filteredAds = data.advertisements.filter((ad) => {
          const appliedApps = ad.applied_apps;
          return appliedApps?.includes('all') || appliedApps?.includes('rc');
        });
        setBannersData(filteredAds);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching banners:', err);
        setError(err.message);
        // Fallback to local banners if API fails
        setBannersData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBanners();
  }, []);

  return { bannersData, isLoading, error };
};

const themedAds = {
  [THEMES.partnersand]: partnersandAds,
  [THEMES.santam]: santamAds,
  [THEMES.aviva]: avivaAds,
  [THEMES.axa]: axaAds,
  [THEMES.us]: usAds,
};

const BannerLink = ({ side, banner }) => (
  <a
    key={banner.key}
    href={
      side === 'rightImage' && banner?.urlRight ? banner.urlRight : banner.url
    }
    target="_blank"
    rel="noopener noreferrer"
  >
    <img src={banner[side]} alt={banner.key} />
  </a>
);

const SideAds = ({ side = 'leftImage' }) => {
  const countryId = useSelector((state) => state.auth?.user?.user?.countryId);
  const { bannersData, isLoading, error } = useBannersData();

  const selectSideAds = () => {
    // Use themed ads if available and not default theme
    if (themedAds[theme.siteTheme] && theme.siteTheme !== THEMES.default) {
      return themedAds[theme.siteTheme].map((banner) => (
        <BannerLink key={banner.key} banner={banner} side={side} />
      ));
    }

    return bannersData?.length > 0
      ? bannersData
          .filter((banner) => banner.country === countryId)
          .map((banner) => (
            <BannerLink key={banner.key} banner={banner} side={side} />
          ))
      : [];
  };

  // Show loading state or empty state if no banners
  const sideAds = selectSideAds();

  if (isLoading) {
    return (
      <div className="side-banner-container">
        <div
          style={{
            height: '600px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          Loading banners...
        </div>
      </div>
    );
  }

  if (error) {
    // eslint-disable-next-line no-console
    console.warn('Banner loading error:', error);
  }

  if (sideAds.length === 0) {
    return (
      <div className="side-banner-container">
        <div style={{ height: '600px' }} />
      </div>
    );
  }

  return (
    <Carousel
      className="side-banner-container"
      effect="fade"
      autoplay
      pauseOnHover={false}
      autoplaySpeed={4500}
      dots={false}
    >
      {sideAds}
    </Carousel>
  );
};

const LayoutWithSideAds = ({ children }) => {
  const { height } = useWindowResize();
  const { scrollPosition } = useWindowScroll();

  const BANNER_IMAGE_HEIGHT = 600 + 20;
  const FOOTER_HEIGHT = 102;

  const getBannerPosition = () => {
    if (height && scrollPosition) {
      if (height >= BANNER_IMAGE_HEIGHT + FOOTER_HEIGHT) return 10;
      if (
        height < BANNER_IMAGE_HEIGHT + FOOTER_HEIGHT &&
        scrollPosition > 360
      ) {
        return height - (BANNER_IMAGE_HEIGHT + FOOTER_HEIGHT);
      }
    }
    return 10;
  };

  return (
    <Row className="with-side-ads">
      <Col span={0} lg={2}>
        <Affix offsetTop={getBannerPosition()}>
          <SideAds side="leftImage" />
        </Affix>
      </Col>
      <Col span={24} lg={20}>
        {children}
      </Col>
      <Col span={0} lg={2}>
        <Affix offsetTop={getBannerPosition()}>
          <SideAds side="rightImage" />
        </Affix>
      </Col>
    </Row>
  );
};

export default LayoutWithSideAds;
